import PropTypes from 'prop-types';
import { allPass, clone, find, propEq, path, includes, reject } from 'ramda';
import React, { Component } from 'react';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardHeader from '@material-ui/core/CardHeader';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/styles';

import Business from '@material-ui/icons/Business';
import Group from '@material-ui/icons/Group';


/**
 * A mapping of roles to user-friendly strings.
 */
const rolesMap = {
  'ADMIN': 'Wachtdienstmedewerker',
  'SUBADMIN': 'Onderaannemer',
  'DRIVER': 'Chauffeur',
  'ASL': 'ASL/Klantenservice Medewerker',
};

class RolesField extends Component {
  static propTypes = {
    input: PropTypes.shape({
      name: PropTypes.string.isRequired,
      onChange: PropTypes.func.isRequired,
      value: PropTypes.any,
    }).isRequired,
    isAdmin: PropTypes.bool.isRequired,
    meta: PropTypes.shape({
      error: PropTypes.string,
      touched: PropTypes.bool.isRequired,
      warning: PropTypes.string,
    }).isRequired,
    label: PropTypes.string.isRequired,
    companies: PropTypes.array.isRequired,
  };

  state = {
    selected: [],
    addRoleOpen: false,
    addRoleAnchorEl: undefined,
    newRole: undefined,
    newRoleError: undefined,
    newCompany: undefined,
    newCompanyError: undefined,
  };

  handleAddRole = event => {
    event.preventDefault();

    this.setState({ addRoleOpen: true, addRoleAnchorEl: event.currentTarget });
  };

  handleAddRoleClose = () => {
    this.setState({ addRoleOpen: false });
  };

  handleNewRole = (event) => {
    this.setState({ newRole: event.target.value, newRoleError: undefined });
  };

  handleNewCompany = (event) => {
    this.setState({ newCompany: event.target.value, newCompanyError: undefined });
  };

  handleAddNewRole = () => {
    const { input: { value, onChange } } = this.props;
    const { newCompany, newRole } = this.state;

    const newValue = clone(value || []);

    if (newRole && newCompany) {
      if (!find(allPass([propEq('role', newRole), propEq('company', newCompany)]))(newValue)) {
        /**
         * Add a new value with special id '-1', which signifies a newly added
         * ApiRole.
         */
        newValue.push({ id: '-1', role: newRole, company: newCompany });

        onChange(newValue);

        this.setState({
          addRoleOpen: false,
          newCompany: undefined,
          newCompanyError: undefined,
          newRole: undefined,
          newRoleError: undefined,
        });
      } else {
        this.setState({ newRoleError: 'Deze combinatie van rol en bedrijf bestaat al' });
      }
    } else {
      /**
       * Do validation of our SelectFields.
       */
      if (!newRole) {
        this.setState({ newRoleError: 'U moet een geldige rol selecteren' });
      }

      if (!newCompany) {
        this.setState({ newCompanyError: 'U moet een geldig bedrijf selecteren' });
      }
    }
  };

  render() {
    const {
      classes, companies,
      input: { name, value, onChange },
      isAdmin,
      meta: { touched, error, warning },
      label,
    } = this.props;

    const {
      addRoleAnchorEl, addRoleOpen,
      newCompany, newRole, newRoleError, newCompanyError,
      selected,
    } = this.state;

    return (
      <Card className={classes.card}>
        {touched && (error || warning) && <CardHeader title={error || warning} />}

        {value && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell><Group />Rol</TableCell>
                <TableCell><Business />Bedrijf</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {value && value.length === 0 && <TableCell colSpan={3}>Geen rollen toegevoegd</TableCell>}

              {value.map((role, i) => (
                <TableRow
                  key={'row-' + i}
                  selected={includes(i, selected)}
                  onClick={() => {
                    let newSelected = clone(selected);
                    if (includes(i, selected)) {
                      newSelected = reject(v => v === i, newSelected);
                    } else {
                      newSelected.push(i);
                    }

                    this.setState({ selected: newSelected });
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={includes(i, selected)} color="primary" />
                  </TableCell>

                  <TableCell>{rolesMap[role.role] || 'Onbekende rol'}</TableCell>
                  <TableCell>{
                    path(['attributes', 'name'], find(propEq('id', role.company), companies) || {}) || 'Onbekend bedrijf'
                  }</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <CardActions>
          <div
            onClick={() => {
              /**
               * Remove all selected roles from our values, and trigger
               * onChange.
               */
              const newValue = value.filter((v, i) => {
                return (selected.indexOf(i) === -1);
              });

              onChange(newValue);

              this.setState({ selected: newValue.filter((v, i) => selected.indexOf(i) !== -1) });
            }}
          >
            <Button variant="contained" color="primary">Verwijder geselecteerde rollen...</Button>
          </div>

          <div onClick={this.handleAddRole}>
            <Button variant="contained" color="primary">Voeg rol toe...</Button>
          </div>

          <Popover
            open={addRoleOpen}
            anchorEl={addRoleAnchorEl}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            onClose={this.handleAddRoleClose}
          >
            <div className={classes.popoverContainer}>
              <FormControl error={!!newRoleError} className={classes.formControl}>
                <InputLabel htmlFor="popover-role">Rol</InputLabel>

                <Select id="popover-role" value={newRole || ''} onChange={this.handleNewRole}>
                  <MenuItem value="" disabled>Kies een rol...</MenuItem>
                  {isAdmin && <MenuItem value="ADMIN">{rolesMap['ADMIN']}</MenuItem>}
                  <MenuItem value="SUBADMIN">{rolesMap['SUBADMIN']}</MenuItem>
                  <MenuItem value="DRIVER">{rolesMap['DRIVER']}</MenuItem>
                  {isAdmin && <MenuItem value="ASL">{rolesMap['ASL']}</MenuItem>}
                </Select>

                {newRoleError && <FormHelperText>{newRoleError}</FormHelperText>}
              </FormControl>

              <FormControl error={!!newCompanyError} className={classes.formControl}>
                <InputLabel htmlFor="popover-company">Bedrijf</InputLabel>

                <Select id="popover-company" value={newCompany || ''} onChange={this.handleNewCompany}>
                  <MenuItem value="" disabled>Kies een bedrijf...</MenuItem>

                  {companies.map(company => (
                    <MenuItem key={'company-' + company.id} value={company.id}>{company.attributes.name}</MenuItem>
                  ))}
                </Select>

                {newCompanyError && <FormHelperText>{newCompanyError}</FormHelperText>}
              </FormControl>

              <div onClick={this.handleAddNewRole}>
                <Button variant="contained" color="primary">Voeg toe...</Button>
              </div>
            </div>
          </Popover>
        </CardActions>
      </Card>
    );
  };
}

export default withStyles({
  card: {
    marginTop: 20,
  },
  popoverContainer: {
    margin: 20,
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    width: 250,
    marginLeft: 20,
    marginBottom: 20,
  },
})(RolesField);
